import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ArrowUpRight, Database, Mail, Phone, Users, Megaphone, Globe2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import BackgroundFX from '../components/BackgroundFX';
import SectionLabel from '../components/SectionLabel';
import Reveal from '../components/Reveal';
import ServiceVideo from '../components/ServiceVideo';
import { CTA_URL, servicesHeroVideo, serviceVideos } from '../mock';
import { useI18n } from '../i18n/I18nContext';

export default function Services() {
  const location = useLocation();
  const { t } = useI18n();
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location]);

  const ANCHORS = [
    { id: 'cegadatbazis', label: t('services_page.anchors.cegadatbazis'), icon: Database },
    { id: 'email-marketing', label: t('services_page.anchors.email'), icon: Mail },
    { id: 'telefonos-megkereses', label: t('services_page.anchors.phone'), icon: Phone },
    { id: 'munkatars-biztositasa', label: t('services_page.anchors.staff'), icon: Users },
    { id: 'marketing', label: t('services_page.anchors.marketing'), icon: Megaphone },
    { id: 'terjeszkedes', label: t('services_page.anchors.expansion'), icon: Globe2 },
  ];

  return (
    <main className="relative">
      {/* Hero */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <video className="absolute inset-0 w-full h-full object-cover object-center"
            autoPlay loop muted defaultMuted playsInline webkit-playsinline="true" preload="auto" disableRemotePlayback aria-hidden="true">
            <source src={servicesHeroVideo.webm} type="video/webm" />
            <source src={servicesHeroVideo.mp4} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-[#ffffff]/15" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#ffffff]/70 via-[#ffffff]/15 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#ffffff]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(37,99,235,0.10),transparent_55%)]" />
          <div className="absolute inset-0 mix-blend-overlay bg-sky-500/10" />
        </div>
        <BackgroundFX />
        <div className="relative z-10 mx-auto max-w-7xl px-5">
          <div className="space-y-8 max-w-5xl">
            <SectionLabel>{t('services_page.hero_tag')}</SectionLabel>
            <h1 className="font-display text-[40px] leading-[1.05] sm:text-[52px] md:text-[64px] lg:text-[72px] font-semibold tracking-tight text-slate-900">
              {t('services_page.hero_title_a')} <br />
              {t('services_page.hero_title_b')} <span className="text-gradient-green glow-text">{t('services_page.hero_title_c')}</span>
            </h1>
            <p className="text-slate-700 text-base md:text-lg max-w-2xl leading-relaxed">{t('services_page.hero_desc')}</p>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {ANCHORS.map((s) => (
              <a key={s.id} href={`#${s.id}`}
                className="group flex items-center gap-2 rounded-xl border border-blue-500/15 bg-white/70 px-3 py-3 hover:border-blue-500/40 hover:bg-blue-50 transition-all">
                <s.icon className="h-4 w-4 text-blue-600 shrink-0" strokeWidth={1.8} />
                <span className="text-xs md:text-sm text-slate-700 group-hover:text-slate-900 truncate">{s.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Cegadatbazis */}
      <section id="cegadatbazis" className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-5">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6 space-y-5">
              <SectionLabel>{t('services_page.cegdb.tag')}</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 leading-tight">{t('services_page.cegdb.title')}</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>{t('services_page.cegdb.p1')}</p>
                <p>{t('services_page.cegdb.p2')}</p>
                <p>{t('services_page.cegdb.p3')}</p>
                <p className="pt-2 font-medium text-slate-900">{t('services_page.cegdb.filters_label')}</p>
                <ul className="space-y-1.5 text-sm">
                  {(t('services_page.cegdb.filters') || []).map((f) => (
                    <li key={f} className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-blue-500" /> {f}</li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-wrap gap-3 pt-4">
                <a href={CTA_URL} target="_blank" rel="noreferrer">
                  <Button className="btn-primary bg-blue-500 hover:bg-blue-600 text-white rounded-xl h-11 px-5 font-medium">
                    {t('common.free_consultation')} <ArrowUpRight className="ml-1.5 h-4 w-4" />
                  </Button>
                </a>
                <Link to="/#kapcsolat">
                  <Button variant="outline" className="rounded-xl h-11 px-5 bg-transparent border-blue-500/25 text-slate-700 hover:bg-blue-500/10 hover:text-slate-900 hover:border-blue-500/50">
                    {t('common.contact')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6 space-y-6">
              <div className="grid grid-cols-2 gap-3">
                {(t('services_page.cegdb.countries') || []).map((d) => (
                  <div key={d.country} className="rounded-2xl border border-blue-500/12 bg-white p-5 hover:border-blue-500/35 card-lift group">
                    <div className="flex items-center justify-between text-[10px] tracking-widest text-blue-600">
                      <span>{d.flag} {d.label}</span>
                      <span className="h-1 w-1 rounded-full bg-blue-500/60 group-hover:bg-blue-500" />
                    </div>
                    <div className="mt-4 font-display text-4xl font-semibold text-gradient-green leading-none">{d.value}</div>
                    <div className="mt-3 text-sm text-slate-700">{d.country}</div>
                  </div>
                ))}
              </div>
              <div className="w-full">
                <ServiceVideo video={serviceVideos.cegadatbazis} label={t('services_page.cegdb.title')} aspect="video" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Divider />

      <ServiceSection anchor="email-marketing" data={t('services_page.email')} video={serviceVideos.email} StepComp={StepCard} />
      <Divider />
      <ServiceSection anchor="telefonos-megkereses" data={t('services_page.phone')} video={serviceVideos.phone} StepComp={FeatureCard} itemsKey="items" />
      <Divider />
      <ServiceSection anchor="munkatars-biztositasa" data={t('services_page.staff')} video={serviceVideos.staff} StepComp={FeatureCard} itemsKey="items" />
      <Divider />
      <ServiceSection anchor="marketing" data={t('services_page.marketing')} video={serviceVideos.marketing} StepComp={FeatureCard} itemsKey="items" />
      <Divider />

      {/* Terjeszkedes — has 8 steps */}
      <section id="terjeszkedes" className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-5">
          <div className="grid lg:grid-cols-12 gap-10 mb-12 items-start">
            <div className="lg:col-span-7 space-y-5">
              <SectionLabel>{t('services_page.expansion.tag')}</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 leading-tight">
                {t('services_page.expansion.title_a')} <span className="text-gradient-green">{t('services_page.expansion.title_b')}</span>
              </h2>
              <p className="text-slate-700 leading-relaxed max-w-lg">{t('services_page.expansion.desc')}</p>
            </div>
            <div className="lg:col-span-5">
              <ServiceVideo video={serviceVideos.expansion} label={t('services_page.expansion.video_label')} aspect="video" />
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {(t('services_page.expansion.steps') || []).map((s, i) => (
              <StepCard key={s.title} step={{ ...s, num: String(i + 1).padStart(2, '0') }} delay={i * 40} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5">
          <div className="relative rounded-[28px] border border-blue-500/15 bg-gradient-to-br from-blue-50 via-white to-white p-10 md:p-16 overflow-hidden">
            <div className="absolute -top-32 -left-32 h-80 w-80 rounded-full bg-blue-500/15 blur-[100px]" />
            <div className="absolute -bottom-32 -right-32 h-80 w-80 rounded-full bg-sky-500/15 blur-[100px]" />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div className="space-y-5">
                <SectionLabel>{t('services_page.final_cta.tag')}</SectionLabel>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-slate-900 leading-[1.05]">
                  {t('services_page.final_cta.title_a')} <br />
                  <span className="text-gradient-green">{t('services_page.final_cta.title_b')}</span>
                </h2>
              </div>
              <div className="space-y-5">
                <p className="text-slate-700 leading-relaxed">{t('services_page.final_cta.desc')}</p>
                <a href={CTA_URL} target="_blank" rel="noreferrer" className="inline-block">
                  <Button className="btn-primary bg-blue-500 hover:bg-blue-600 text-white rounded-xl h-12 px-6 font-medium text-[15px]">
                    {t('common.free_consultation')} <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function ServiceSection({ anchor, data, video, StepComp, itemsKey = 'steps' }) {
  if (!data) return null;
  const items = data[itemsKey] || [];
  return (
    <section id={anchor} className="py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-5">
        <div className="grid lg:grid-cols-12 gap-10 mb-12 items-start">
          <div className="lg:col-span-7 space-y-5">
            <SectionLabel>{data.tag}</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 leading-tight">{data.title}</h2>
            <p className="text-slate-700 leading-relaxed max-w-lg">{data.desc}</p>
          </div>
          <div className="lg:col-span-5">
            <ServiceVideo video={video} label={data.video_label} aspect="video" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((s, i) => (
            <StepComp key={s.title} step={{ ...s, num: String(i + 1).padStart(2, '0') }} item={s} delay={i * 40} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Divider() {
  return (
    <div className="mx-auto max-w-7xl px-5">
      <div className="section-divider" />
    </div>
  );
}

function StepCard({ step, delay }) {
  return (
    <Reveal delay={delay}>
      <div className="group h-full rounded-2xl border border-blue-500/10 bg-white p-6 hover:border-blue-500/30 card-lift">
        <div className="flex items-baseline justify-between mb-4">
          <span className="font-mono text-xs tracking-widest text-blue-600">{step.num}</span>
          <span className="h-1 w-6 bg-gradient-to-r from-transparent to-blue-500/60 group-hover:to-blue-500 transition-colors" />
        </div>
        <h3 className="font-display text-lg font-medium text-slate-900 mb-2 leading-snug">{step.title}</h3>
        <p className="text-sm text-slate-700 leading-relaxed">{step.desc}</p>
      </div>
    </Reveal>
  );
}

function FeatureCard({ item, delay }) {
  return (
    <Reveal delay={delay}>
      <div className="group h-full rounded-2xl border border-blue-500/10 bg-white p-6 hover:border-blue-500/30 hover:bg-slate-50 card-lift">
        <div className="flex items-start gap-3 mb-3">
          <span className="mt-1 h-2 w-2 rounded-full bg-blue-500 group-hover:shadow-[0_0_10px_rgba(37,99,235,0.9)] transition-all" />
          <h3 className="font-display text-lg font-medium text-slate-900 leading-snug">{item.title}</h3>
        </div>
        <p className="text-sm text-slate-700 leading-relaxed pl-5">{item.desc}</p>
      </div>
    </Reveal>
  );
}
